import React from "react";
import { useSelector } from "react-redux";

import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForClasses } from "../../../utils/confighelpers";
import { Card, FcDrawer, FcModal, FieldError, FieldLabel, Icon, inputClass } from "../schedule/ui";
import { euro, KIND, periodText, toNumber } from "./productUtils";

/**
 * Add / edit a product. Same endpoints and the same stored fields as the old forms:
 *  - new:  POST products/add          (old defaults for each type)
 *  - edit: POST products/update/:id   (only productName, productDesc, productType, productFields, price, availableForSale)
 * The backend syncs Stripe after each save: a price change archives the old Stripe price and creates a new one,
 * so existing subscribers keep their price and only new purchases pay the new one.
 * Billing period is fixed after creation: the backend only makes a new Stripe price when the amount changes.
 * Subscription price change with current subscribers (2026-10-26): the club chooses
 *  - keep: current subscribers stay on their price (default, as before), or
 *  - move: after saving, POST products/subscriberPriceChange/:id { mode: "run" } in batches. Each member is moved
 *    by Stripe at their first renewal at least 30 days away and gets an email (contact the club to cancel).
 */

const VALIDITY = [
  [0, "Until used"],
  [30, "30 days"],
  [60, "60 days"],
  [90, "90 days"],
  [120, "4 months"],
  [180, "6 months"],
  [365, "1 year"],
  [-1, "Custom"],
];
const BILLING = [
  ["week", "Week"],
  ["month", "Month"],
  ["year", "Year"],
];

const TICKET = <path d="M3 9a2 2 0 0 0 0 6v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0 0-6V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z" />;

/** Old "Credit Pass" defaults. */
const PASS_FIELDS = { passName: "", noOfAllowedSessions: 10, validityDays: 30, validUntilConsumed: true, allowAllClasses: true, classId: [] };
/** Old "Subscription Pass" defaults. */
const SUB_FIELDS = {
  passName: "",
  noOfAllowedSessions: "0",
  validityDays: 0,
  validUntilConsumed: true,
  allowAllClasses: true,
  classId: [],
  billingPeriod: { interval: "month", interval_count: 1 },
  unlimitedCredits: true,
  creditsRenewalSessions: 0,
  creditsRenewalCycle: "",
};

const formFromProduct = (kind, p) => {
  const f = p?.productFields || {};
  const days = parseInt(f.validityDays, 10) || 0;
  const until = p ? f.validUntilConsumed !== false : true; // old default: valid until used
  const preset = VALIDITY.some(([d]) => d === days && d > 0);
  return {
    name: p?.productName || "",
    desc: p?.productDesc || "",
    price: p ? String(p.price ?? "") : kind === "free" ? "0" : "",
    credits: p ? parseInt(f.noOfAllowedSessions, 10) || 1 : kind === "free" ? 50 : 10,
    validity: until ? 0 : p ? (preset ? days : -1) : 30,
    customDays: p && !until && !preset ? String(days) : "45",
    billing: f.billingPeriod?.interval || "month",
    allClasses: f.allowAllClasses !== false,
    classIds: Array.isArray(f.classId) ? f.classId.map(String) : [],
    onSale: p ? p.availableForSale !== false : kind !== "free",
  };
};

export default function ProductFormDrawer({ open, kind = "pass", product = null, onClose, onSaved }) {
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const editing = !!product;
  const [f, setF] = React.useState(() => formFromProduct(kind, product));
  const [errors, setErrors] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [classes, setClasses] = React.useState(null);
  // Price change of a subscription with current subscribers: { subscribers, noticeDays } -> choice -> progress -> result
  const [priceStep, setPriceStep] = React.useState(null);
  const [choice, setChoice] = React.useState("keep");
  const [moveRun, setMoveRun] = React.useState(null); // { running, total, moved:[], skipped:[], failed:[] }

  React.useEffect(() => {
    if (!open) return;
    setF(formFromProduct(kind, product));
    setErrors({});
    setPriceStep(null);
    setChoice("keep");
    setMoveRun(null);
  }, [open, kind, product]);

  React.useEffect(() => {
    if (!open || classes) return;
    (async () => {
      try {
        const res = await axiosInstance({
          method: "get",
          url: `${getUrlForClasses()}listOfClasses?isPackage=${false}&listFor=${userReduxState.businessType}&onlyClassDetail=true`,
        });
        const list = res?.status === 200 && Array.isArray(res?.data?.data) ? res.data.data : [];
        setClasses(list.map((c) => ({ id: String(c._id), name: c.className })));
      } catch (e) {
        setClasses([]);
      }
    })();
  }, [open, classes, userReduxState.businessType]);

  const ERROR_KEY = { name: "name", price: "price", credits: "credits", validity: "days", customDays: "days", allClasses: "classes", classIds: "classes" };
  const set = (patch) => {
    setF((v) => ({ ...v, ...patch }));
    const clear = Object.keys(patch).map((k) => ERROR_KEY[k]).filter(Boolean);
    if (clear.length) setErrors((e) => (clear.some((k) => e[k]) ? Object.fromEntries(Object.entries(e).filter(([k]) => !clear.includes(k))) : e));
  };
  const isPass = kind === "pass";
  const isSub = kind === "sub";
  const isFree = kind === "free";
  const price = toNumber(f.price);
  const days = f.validity === -1 ? parseInt(f.customDays, 10) || 0 : f.validity;
  const K = KIND[kind];

  // Classes picked earlier that no longer exist in the class list are kept (and shown) so nothing is lost.
  const classOptions = React.useMemo(() => {
    const list = classes || [];
    const names = Object.fromEntries((product?.productFields?.classList || []).map((c) => [String(c.classId), c.className]));
    const extra = f.classIds.filter((id) => !list.some((c) => c.id === id)).map((id) => ({ id, name: names[id] || "Earlier activity" }));
    return [...list, ...extra];
  }, [classes, f.classIds, product]);

  const validate = () => {
    const e = {};
    if (!f.name.trim()) e.name = "Give the product a name.";
    if (!isFree && !(price > 0)) e.price = "Enter a price above €0.";
    if ((isPass || isFree) && !(f.credits >= 1)) e.credits = "At least 1.";
    if ((isPass || isFree) && f.validity === -1 && !(/^\d+$/.test(String(f.customDays).trim()) && parseInt(f.customDays, 10) > 0)) e.days = "Enter a whole number of days.";
    if (!f.allClasses && f.classIds.length === 0) e.classes = "Pick at least one activity.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const priceChangeUrl = product ? `${getUrlForClasses("")}products/subscriberPriceChange/${product._id}` : "";

  /** Save button: a subscription whose price changes and has current subscribers asks keep / move first. */
  const save = async () => {
    if (!validate()) return;
    if (editing && isSub && toNumber(product?.price) !== price && !priceStep) {
      setBusy(true);
      let info = null;
      try {
        const res = await axiosInstance({ method: "post", url: priceChangeUrl, data: { mode: "count" } });
        if (res?.data?.statusCode === 200) info = res.data.data;
      } catch (e) {
        info = null;
      }
      setBusy(false);
      if (info && info.subscribers > 0) {
        setChoice("keep");
        setPriceStep({ subscribers: info.subscribers, noticeDays: info.noticeDays || 30 });
        return;
      }
    }
    await saveProduct();
  };

  /** Moves the current subscribers in batches (the server handles a few per call). */
  const moveSubscribers = async (retryFailed = false) => {
    const acc = { running: true, total: priceStep?.subscribers || 0, moved: [], skipped: [], failed: [] };
    if (retryFailed && moveRun) {
      acc.moved = moveRun.moved;
      acc.skipped = moveRun.skipped;
    }
    setMoveRun({ ...acc });
    let first = true;
    for (let guard = 0; guard < 200; guard++) {
      let data = null;
      try {
        const res = await axiosInstance({ method: "post", url: priceChangeUrl, data: { mode: "run", retryFailed: retryFailed && first } });
        if (res?.data?.statusCode === 200) data = res.data.data;
        else acc.error = typeof res?.data?.message === "string" ? res.data.message : "Couldn't update the subscribers.";
      } catch (e) {
        acc.error = "Couldn't reach the server. Members already handled keep their change; try again for the rest.";
      }
      first = false;
      if (!data) break;
      acc.moved = [...acc.moved, ...(data.moved || [])];
      acc.skipped = [...acc.skipped, ...(data.skipped || [])];
      acc.failed = [...acc.failed.filter((x) => !(data.moved || []).some((m) => String(m.purchaseId) === String(x.purchaseId))), ...(data.failed || [])];
      setMoveRun({ ...acc });
      const handled = (data.moved || []).length + (data.skipped || []).length + (data.failed || []).length;
      if (!data.remaining || handled === 0) break;
    }
    setMoveRun({ ...acc, running: false });
  };

  const saveProduct = async (moveAfter = false) => {
    const base = product?.productFields ? { ...product.productFields } : { ...(isSub ? SUB_FIELDS : PASS_FIELDS) };
    delete base.classList; // added by the list endpoint, not stored
    const productFields = {
      ...base,
      passName: f.name.trim(),
      allowAllClasses: f.allClasses,
      classId: f.allClasses ? [] : f.classIds,
    };
    if (!isSub) {
      productFields.noOfAllowedSessions = parseInt(f.credits, 10);
      productFields.validUntilConsumed = days === 0;
      productFields.validityDays = days;
    } else if (!editing) {
      productFields.billingPeriod = { interval: f.billing, interval_count: 1 };
    }
    const body = {
      productType: isSub ? "subscription" : "classpass",
      productName: f.name.trim(),
      productDesc: f.desc.trim(),
      productFields,
      price: isFree ? 0 : price,
      // Free credit passes are never sold (there is no Stripe price for €0). An old €0 product keeps its setting.
      availableForSale: isFree ? (editing ? product.availableForSale !== false && f.onSale : false) : f.onSale,
      ...(!editing && isSub ? { stripProductDetail: { priceData: [] } } : {}),
    };
    setBusy(true);
    try {
      const res = await axiosInstance({
        method: "post",
        url: editing ? `${getUrlForClasses("")}products/update/${product._id}` : `${getUrlForClasses("")}products/add`,
        data: body,
      });
      if (res?.status === 200 && res?.data?.statusCode === 200) {
        if (moveAfter) {
          onSaved?.();
          await moveSubscribers(false); // the pop-up shows progress, then the result
          return;
        }
        setPriceStep(null);
        addSuccessToast(editing ? "Changes saved" : `${body.productName} created`);
        onSaved?.();
        onClose();
      } else {
        addErrorToast(typeof res?.data?.message === "string" && res.data.message ? res.data.message : "Couldn't save the product. Please try again.");
      }
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const pill = (on) =>
    classNames(
      "h-10 rounded-full border px-4 text-sm font-extrabold transition-colors",
      on ? "border-indigo-bz bg-[#F3E9FF] text-[#6A00BA]" : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
    );
  const seg = (on) => classNames("h-9 flex-1 rounded-xl px-3 text-sm font-extrabold", on ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900");

  const pickedNames = classOptions.filter((c) => f.classIds.includes(c.id)).map((c) => c.name);
  const validityLine = days === 0 ? "valid until used" : days === 365 ? "valid 1 year" : `valid ${days} days`;
  const billingNow = editing ? product?.productFields?.billingPeriod : { interval: f.billing, interval_count: 1 };
  const pvLine = isSub
    ? `Unlimited credits · renews every ${periodText(billingNow)}`
    : isFree
      ? `Given by the club · ${validityLine}`
      : `${f.credits} ${f.credits === 1 ? "credit" : "credits"} · ${validityLine}${!f.allClasses ? " · " + (pickedNames.join(", ") || "pick activities") : ""}`;

  const title = editing ? f.name || K.label : isPass ? "New credit pass" : isSub ? "New subscription" : "New free credit pass";
  const subtitle = isPass ? "Members buy it once and use the credits to book." : isSub ? "Members pay automatically every period." : "For credits you give away or import.";
  const priceChanged = editing && !isFree && toNumber(product?.price) !== price;

  return (
    <FcDrawer
      open={open}
      onClose={onClose}
      eyebrow={`${editing ? "EDIT" : "NEW"} ${K.label.toUpperCase()}`}
      title={title}
      subtitle={subtitle}
      primaryLabel={editing ? "Save changes" : isPass ? "Create credit pass" : isSub ? "Create subscription" : "Create free pass"}
      onPrimary={save}
      busy={busy}
      footerNote={editing && !isFree ? (priceChanged ? <span className="font-bold text-[#7A4A00]">{isSub ? "New members pay the new price. You'll choose about current subscribers." : "New price applies to new purchases only."}</span> : isSub ? "A new price applies to new members; you choose about current subscribers." : "Price changes apply to new purchases only.") : null}
    >
      {/* What members see */}
      <div className="flex items-center gap-3.5 rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-4 py-3.5">
        <span className={classNames("flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl", K.chip)}>
          <Icon d={TICKET} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-gray-500">What members see</p>
          <p className="truncate text-base font-black text-gray-900">{f.name || (isPass ? "Your credit pass" : isSub ? "Your subscription" : "Your free credits")}</p>
          <p className="truncate text-sm text-gray-500">{pvLine}</p>
        </div>
        <div className="text-right">
          <p className="text-xl font-black text-gray-900">{isFree ? "€0" : euro(f.price)}</p>
          <p className="text-xs font-bold text-gray-500">{isSub ? `per ${periodText(billingNow)}` : isFree ? "not for sale" : "one-time"}</p>
        </div>
      </div>

      <Card title="About the product">
        <div className="flex flex-col gap-4">
          <div>
            <FieldLabel htmlFor="pf-name">Name</FieldLabel>
            <input
              id="pf-name"
              className={inputClass(errors.name)}
              value={f.name}
              maxLength={80}
              onChange={(e) => set({ name: e.target.value })}
              placeholder={isPass ? "e.g. 10-credit pass" : isSub ? "e.g. Monthly unlimited" : "e.g. Migration credits"}
            />
            <FieldError>{errors.name}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="pf-desc" optional>Description</FieldLabel>
            <textarea id="pf-desc" rows={3} className={classNames(inputClass(false), "resize-none")} value={f.desc} onChange={(e) => set({ desc: e.target.value })} placeholder="What's included, who it's for" />
          </div>
        </div>
      </Card>

      {!isFree && (
        <Card title={isSub ? "Price & billing" : "Price & classes"}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor="pf-price">{isSub ? "Price per period" : "Price"}</FieldLabel>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-base font-extrabold text-gray-500">€</span>
                <input id="pf-price" inputMode="decimal" className={classNames(inputClass(errors.price), "pl-9")} value={f.price} onChange={(e) => set({ price: e.target.value.replace(/[^\d.,]/g, "") })} placeholder="0" />
              </div>
              {errors.price ? <FieldError>{errors.price}</FieldError> : <p className="mt-1.5 text-xs text-gray-500">Members pay this. Stripe and FitClub fees come off your payout.</p>}
            </div>
            {isPass && (
              <div>
                <FieldLabel>Classes included</FieldLabel>
                <Stepper value={f.credits} onChange={(credits) => set({ credits })} />
                <p className="mt-1.5 text-xs text-gray-500">{f.credits > 1 && price > 0 ? `${euro(price / f.credits)} per credit` : "One credit"}</p>
              </div>
            )}
            {isSub && (
              <div>
                <FieldLabel>Credits per period</FieldLabel>
                <div className="flex h-12 items-center rounded-2xl bg-[#F3E9FF] px-4 text-base font-black text-[#6A00BA]">
                  {product?.productFields?.unlimitedCredits === false ? `${product.productFields.creditsRenewalSessions || 0} credits` : "Unlimited"}
                </div>
              </div>
            )}
          </div>
          {isSub && (
            <div className="mt-4">
              <FieldLabel>Billed every</FieldLabel>
              {editing ? (
                <>
                  <div className="flex h-10 w-fit items-center rounded-full border border-gray-200 bg-[#F7F4F8] px-4 text-sm font-extrabold capitalize text-gray-700">{periodText(billingNow)}</div>
                  <p className="mt-1.5 text-xs text-gray-500">The billing period can't change after you create a subscription. Create a new one for a different period.</p>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {BILLING.map(([k, l]) => (
                      <button key={k} type="button" aria-pressed={f.billing === k} onClick={() => set({ billing: k })} className={pill(f.billing === k)}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">Members are charged automatically until they or you cancel. You can't change this later.</p>
                </>
              )}
            </div>
          )}
        </Card>
      )}

      {isFree && (
        <Card>
          <div className="flex gap-3.5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#E7F0FF] text-[#1D4ED8]">
              <Icon d={<><circle cx="12" cy="12" r="9" /><path d="M12 8h.01M11 12h1v4h1" /></>} />
            </span>
            <div>
              <p className="text-base font-extrabold text-gray-900">Free credit passes cost €0 and are never sold</p>
              <p className="mt-1 text-sm text-gray-600">Use one to give credits to a member (Give credits on their page) or to import members with the credits they had elsewhere.</p>
            </div>
          </div>
          <div className="mt-4">
            <FieldLabel>Most credits you can give at once</FieldLabel>
            <Stepper value={f.credits} onChange={(credits) => set({ credits })} max={500} />
            <FieldError>{errors.credits}</FieldError>
          </div>
        </Card>
      )}

      {!isSub && (
        <Card>
          <h3 className="text-base font-black text-gray-900">How long can they use it?</h3>
          <p className="mb-4 mt-1 text-sm text-gray-500">Counted from the day they get it.</p>
          <div className="flex flex-wrap gap-2">
            {VALIDITY.map(([d, l]) => (
              <button key={d} type="button" aria-pressed={f.validity === d} onClick={() => set({ validity: d })} className={pill(f.validity === d)}>
                {l}
              </button>
            ))}
          </div>
          {f.validity === -1 && (
            <div className="mt-3 flex items-center gap-3">
              <input aria-label="Number of days" inputMode="numeric" className={classNames(inputClass(errors.days), "!w-28")} value={f.customDays} onChange={(e) => set({ customDays: e.target.value.replace(/\D/g, "") })} />
              <span className="text-sm font-bold text-gray-600">days</span>
            </div>
          )}
          <FieldError>{errors.days}</FieldError>
        </Card>
      )}

      <Card>
        <h3 className="text-base font-black text-gray-900">Which activities can they book?</h3>
        <p className="mb-4 mt-1 text-sm text-gray-500">New activities you add later are included when you choose All activities.</p>
        <div className="mb-3 flex gap-1 rounded-2xl bg-[#F3EEF5] p-1">
          <button type="button" className={seg(f.allClasses)} onClick={() => set({ allClasses: true })}>
            All activities
          </button>
          <button type="button" className={seg(!f.allClasses)} onClick={() => set({ allClasses: false })}>
            Specific activities
          </button>
        </div>
        {!f.allClasses &&
          (classes === null ? (
            <div className="flex gap-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span key={i} className="h-10 w-28 animate-pulse rounded-full bg-[#F3EEF5]" />
              ))}
            </div>
          ) : classOptions.length === 0 ? (
            <p className="text-sm text-gray-600">You have no activities yet. Add one under My Classes first.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {classOptions.map((c) => {
                const on = f.classIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => set({ classIds: on ? f.classIds.filter((x) => x !== c.id) : [...f.classIds, c.id] })}
                    className={classNames(pill(on), "flex items-center gap-1.5")}
                  >
                    {on && <Icon className="h-4 w-4" d={<path d="m5 12 5 5L20 7" />} />}
                    {c.name}
                  </button>
                );
              })}
            </div>
          ))}
        <FieldError>{errors.classes}</FieldError>
      </Card>

      {(!isFree || (editing && product?.availableForSale !== false)) && (
        <Card>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-base font-extrabold text-gray-900">On sale in the app and webshop</p>
              <p className="mt-0.5 text-sm text-gray-500">
                {isFree ? "Free passes can't be bought. Turn this off so members don't see it." : "Turn off to sell it only yourself, with a payment link or invoice from a member's page."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={f.onSale}
              aria-label="On sale"
              disabled={isFree && !f.onSale}
              onClick={() => set({ onSale: !f.onSale })}
              className={classNames("flex h-[30px] w-[52px] shrink-0 rounded-full p-[3px] transition-colors", f.onSale ? "justify-end bg-indigo-bz" : "justify-start bg-[#D9D2DC]")}
            >
              <span className="h-6 w-6 rounded-full bg-white shadow" />
            </button>
          </div>
        </Card>
      )}
      <PriceChangeModal
        open={!!priceStep}
        product={product}
        newPrice={price}
        info={priceStep}
        choice={choice}
        setChoice={setChoice}
        busy={busy}
        run={moveRun}
        onCancel={() => {
          if (busy || moveRun?.running) return;
          if (moveRun) {
            setPriceStep(null);
            onClose();
          } else setPriceStep(null);
        }}
        onConfirm={async () => {
          if (moveRun && !moveRun.running) {
            setPriceStep(null);
            addSuccessToast("Price saved");
            onClose();
            return;
          }
          await saveProduct(choice === "move");
        }}
        onRetry={() => moveSubscribers(true)}
      />
    </FcDrawer>
  );
}

/** Keep current subscribers on their price, or move them with notice; then progress and the result. */
function PriceChangeModal({ open, product, newPrice, info, choice, setChoice, busy, run, onCancel, onConfirm, onRetry }) {
  const oldPrice = toNumber(product?.price);
  const period = periodText(product?.productFields?.billingPeriod);
  const n = info?.subscribers || 0;
  const members = `${n} ${n === 1 ? "member has" : "members have"}`;
  const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const card = (key, title, text) => (
    <label
      key={key}
      className={classNames(
        "flex cursor-pointer gap-3 rounded-2xl border p-4",
        choice === key ? "border-indigo-bz bg-[#FBF8FE] ring-1 ring-indigo-bz" : "border-gray-200 bg-white hover:border-gray-300"
      )}
    >
      <input type="radio" name="price-change" className="mt-1 h-4 w-4 shrink-0 accent-[#7e00dd]" checked={choice === key} onChange={() => setChoice(key)} />
      <span className="min-w-0">
        <span className="block text-[15px] font-extrabold text-gray-900">{title}</span>
        <span className="mt-0.5 block text-sm text-gray-600">{text}</span>
      </span>
    </label>
  );

  if (run) {
    const done = run.moved.length + run.skipped.length + run.failed.length;
    const firstDate = run.moved.length ? run.moved.map((m) => new Date(m.effectiveAt)).sort((a, b) => a - b)[0] : null;
    return (
      <FcModal
        open={open}
        onClose={onCancel}
        eyebrow="PRICE CHANGE"
        title={run.running ? "Updating your subscribers…" : "Subscribers updated"}
        subtitle={`${product?.productName || "Subscription"} · ${euro(oldPrice)} → ${euro(newPrice)} per ${period}`}
        primaryLabel={run.running ? "Please wait…" : "Done"}
        primaryDisabled={run.running}
        busy={run.running}
        onPrimary={onConfirm}
      >
        <div>
          <div className="flex items-baseline justify-between text-sm font-bold text-gray-700">
            <span>{done} of {Math.max(run.total, done)} handled</span>
            {run.running && <span className="text-gray-500">Don't close this window</span>}
          </div>
          <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-[#F3EEF5]">
            <div className="h-full rounded-full bg-indigo-bz transition-all" style={{ width: `${Math.min(100, Math.round((done / Math.max(run.total, done, 1)) * 100))}%` }} />
          </div>
        </div>
        {!run.running && (
          <div className="flex flex-col gap-3 text-sm">
            {run.moved.length > 0 && (
              <div className="rounded-2xl bg-[#E8F7EE] px-4 py-3 text-[#1F6B3A]">
                <p className="font-extrabold">{run.moved.length} {run.moved.length === 1 ? "member moves" : "members move"} to {euro(newPrice)}</p>
                <p className="mt-0.5">Each at their first renewal at least {info?.noticeDays || 30} days from today{firstDate ? `, the first on ${fmtDate(firstDate)}` : ""}. {run.moved.filter((m) => m.emailed).length} emailed{run.moved.some((m) => !m.emailed) ? `; ${run.moved.filter((m) => !m.emailed).length} have no email address, tell them yourself` : ""}.</p>
              </div>
            )}
            {run.skipped.length > 0 && (
              <div className="rounded-2xl border border-gray-200 px-4 py-3">
                <p className="font-extrabold text-gray-900">{run.skipped.length} not moved</p>
                <ul className="mt-1 list-disc pl-5 text-gray-600">
                  {run.skipped.slice(0, 8).map((x) => <li key={String(x.purchaseId)}>{x.name}: {x.reason}</li>)}
                  {run.skipped.length > 8 && <li>and {run.skipped.length - 8} more</li>}
                </ul>
              </div>
            )}
            {run.failed.length > 0 && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                <p className="font-extrabold">{run.failed.length} couldn't be moved (they stay on {euro(oldPrice)})</p>
                <ul className="mt-1 list-disc pl-5">
                  {run.failed.slice(0, 8).map((x) => <li key={String(x.purchaseId)}>{x.name}: {x.reason}</li>)}
                </ul>
                <button type="button" onClick={onRetry} className="mt-2 rounded-xl bg-white px-3 py-1.5 text-sm font-extrabold text-red-800 hover:bg-red-100">Try again</button>
              </div>
            )}
            {run.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 font-semibold text-red-700">{run.error}</p>}
            {run.moved.length === 0 && run.skipped.length === 0 && run.failed.length === 0 && !run.error && <p className="text-gray-600">There was no one to move.</p>}
          </div>
        )}
      </FcModal>
    );
  }

  return (
    <FcModal
      open={open}
      onClose={onCancel}
      eyebrow="PRICE CHANGE"
      title={`Change the price to ${euro(newPrice)}?`}
      subtitle={`${members} ${product?.productName || "this subscription"} now, at ${euro(oldPrice)} per ${period}.`}
      primaryLabel={choice === "move" ? "Save and move members" : "Save new price"}
      busy={busy}
      onPrimary={onConfirm}
    >
      <p className="text-sm text-gray-600">New members pay {euro(newPrice)} from now on. What about the members who already have it?</p>
      <div className="flex flex-col gap-2.5">
        {card("keep", `Keep their current price (${euro(oldPrice)})`, "Nothing changes for them. Only new members pay the new price.")}
        {card(
          "move",
          `Move them to ${euro(newPrice)}, with notice`,
          `Each member gets an email now. Their new price starts at their first renewal at least ${info?.noticeDays || 30} days from today. Members who'd rather stop can contact you to cancel.`
        )}
      </div>
    </FcModal>
  );
}

function Stepper({ value, onChange, max = 200 }) {
  const btn = "flex h-12 w-12 items-center justify-center rounded-2xl border border-gray-200 bg-white text-2xl font-extrabold text-gray-700 hover:bg-gray-50 disabled:opacity-40";
  return (
    <div className="flex items-center gap-2.5">
      <button type="button" aria-label="Fewer" disabled={value <= 1} onClick={() => onChange(Math.max(1, value - 1))} className={btn}>
        −
      </button>
      <input
        aria-label="Number"
        inputMode="numeric"
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value.replace(/\D/g, ""), 10);
          onChange(Number.isFinite(n) ? Math.min(max, n) : 0);
        }}
        className="h-12 w-16 rounded-2xl border border-gray-200 bg-white text-center text-xl font-black text-gray-900 outline-none focus:border-indigo-bz focus:ring-4 focus:ring-[#F3E9FF]"
      />
      <button type="button" aria-label="More" disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} className={btn}>
        +
      </button>
    </div>
  );
}
